/**
 * How much room the docked assistant takes out of the app.
 *
 * The panel is a column inside the scrolling area rather than a layer over it,
 * so the shift is just flex doing its job — this width is the whole mechanism.
 * An earlier version carved the same number out of the shell and left the
 * panel fixed on top, which stranded the page's scrollbar 460px from the
 * window edge.
 */

/** Width of the docked column. Wide enough for a paragraph, narrow enough to work beside. */
export const DOCKED_WIDTH = 460;

/** The width of the expanded nav, from SidebarContainer. Part of the sum below. */
const SIDEBAR_WIDTH = 250;

/**
 * The narrowest column a page can be given and still lay itself out.
 *
 * This is now a real floor rather than a guess, because the pages reflow: the
 * dashboard queries its own container, so at 520px it drops to two stat cards
 * across and stacks the rail under the main content instead of beside it.
 * Below roughly this, even the stacked layout has nowhere to go.
 */
const MIN_PAGE_WIDTH = 520;

/**
 * Below this the panel covers the page instead of taking room from it.
 *
 * Derived rather than chosen: what matters is what is LEFT once the nav and
 * the panel have taken theirs.
 *
 * It used to have to be 1710, because pages sized themselves off the VIEWPORT
 * and could not tell that the assistant had taken 460px from their column — a
 * 1435px window rendered its widest three-column layout into 725px of space.
 * Now that the dashboard answers to its container, the panel can dock on any
 * screen with room for a usable column, and the page simply narrows.
 */
export const DOCK_MIN_VIEWPORT = SIDEBAR_WIDTH + MIN_PAGE_WIDTH + DOCKED_WIDTH;

/**
 * The height of the scrolling shell the docked panel lives in.
 *
 * It has to be the SCROLLPORT's height, not the viewport's. A sticky element
 * taller than the box it scrolls inside cannot stick to the top of it — the
 * browser has to scroll it to let its bottom edge be reached, so the panel
 * drifted up the screen with the page. At 100vh against a scrollport of
 * 100svh minus a rem, it missed by exactly that rem, which is enough.
 *
 * Kept beside the width because it is the same fact about the same shell:
 * this must match SidebarInset's `h-[calc(100svh-1rem)]` in the routers.
 */
export const SHELL_HEIGHT = 'calc(100svh - 1rem)';
